import { css } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';

import { usePropertyProject, useScanEditingContextSlice, useScopeReportData } from '@kaiizen/kai-pro-api-react-state';
import {
  buildChangeOrderLookupFromLineItems,
  CONTRACTOR_LEGAL_DISCLAIMER,
  getUserFeedbackBannerColorScopeState,
  getUserFeedbackDescriptionScopeState,
  GroupingMode,
  REACT_PRINT_STYLES,
  ReportType,
  useReportDataPipeline,
} from '@kaiizen/kai-reports';

import { logError } from '../Lib/logger';
import { COLORS2, LAYOUT2 } from '../Styles';
import { ArrowUpIcon } from '../Widgets';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ReportSection } from './ScopePrintPreview/ReportSection';
import { ScopePrintPreviewProvider } from './ScopePrintPreview/ScopePrintPreviewContext';
import { ScopeToolbar } from './ScopePrintPreview/ScopeToolbar';
import { useReportNavigation } from './ScopePrintPreview/useReportNavigation';
import { useScrollAndHighlight } from './ScopePrintPreview/useScrollAndHighlight';

const PRINT_PREVIEW_CONTAINER_STYLE = css`
  label: PRINT_PREVIEW_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${COLORS2.alwaysDark.background.dark};
`;

const SCROLL_CONTAINER_WRAPPER_STYLE = css`
  label: SCROLL_CONTAINER_WRAPPER_STYLE;
  position: relative;
  flex: 1;
  overflow: hidden;
`;

const CONTENT_SCROLL_STYLE = css`
  label: CONTENT_SCROLL_STYLE;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  background: ${COLORS2.alwaysDark.background.dark};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const CONTENT_WRAPPER_STYLE = css`
  label: CONTENT_WRAPPER_STYLE;
  display: flex;
  flex-direction: column;
`;

const PAGE_CONTAINER_STYLE = css`
  label: PAGE_CONTAINER_STYLE;
  width: 11in;
  min-height: 8.5in;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media print {
    width: 100%;
    min-height: auto;
    box-shadow: none;
    padding: 0;
  }
`;

const FOOTER_STYLE = css`
  label: FOOTER_STYLE;
  width: 11in;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media print {
    width: 100%;
    box-shadow: none;
    margin-top: 0;
  }
`;

const USER_FEEDBACK_BANNER_STYLE = (scanLifecycleStatus?: string, reportType?: ReportType, scanScopeState?: string) =>
  css`
  label: USER_FEEDBACK_BANNER_STYLE;
  width: 11in;
  background: ${getUserFeedbackBannerColorScopeState(reportType, scanScopeState)};
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media print {
    display: none;
  }
`;

const BANNER_TEXT_STYLE = css`
  label: BANNER_TEXT_STYLE;
  color: ${COLORS2.alwaysDark.white};
  font-size: ${pxToRem(14)};
  font-weight: 400;
  line-height: 1.4;
  margin: 0;
  text-align: center;

  strong {
    font-weight: 600;
  }
`;

const LOADING_STYLE = css`
  label: LOADING_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: ${COLORS2.alwaysDark.text.white};
`;

const SCROLL_TO_TOP_BUTTON_STYLE = css`
  label: SCROLL_TO_TOP_BUTTON_STYLE;
  position: absolute;
  top: ${LAYOUT2.spacing2}px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${COLORS2.alwaysDark.background.black};
  border: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  cursor: pointer;
  transition: opacity 0.3s ease, background 0.2s ease;
  color: ${COLORS2.alwaysDark.text.white};
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;

  &:hover {
    background: ${COLORS2.alwaysDark.background.dark};
    border-color: ${COLORS2.alwaysDark.text.lightGray};
  }

  @media print {
    display: none;
  }
`;

export const ScopePrintPreview = ({ scanId, scanName, reportType = 'fullScope' }: { scanId: string; scanName: string; reportType?: ReportType }) => {
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('group');
  const [currentDropdown, _setCurrentDropdown] = useState<{ groupId: string; taskId?: string } | undefined>();

  const { scan, scanRoot, project, scanScopeState } = useScanEditingContextSlice(['scan', 'scanRoot', 'project', 'scanScopeState']);
  const { refetch: refetchPropertyProject } = usePropertyProject(scan?.propertyProjectId);
  const { reportData, diffReportData, isLoading: isReportDataLoading, error: reportDataError, isError: isReportDataError } = useScopeReportData();

  // same query key as ScanEditingContextProvider's usePropertyProject;
  // but we don't want to force ScanEditingContextProvider to ALWAYS
  // refetch the project when scan state changes so we are forcing it only for this view
  // so project pointers (latestScan, latestApprovedScan) from DerivedProjectState will match the server
  useEffect(() => {
    if (!scan?.propertyProjectId) return;
    void refetchPropertyProject();
  }, [scan?.propertyProjectId, refetchPropertyProject]);

  const { scopeLineItems, diffScopeLineItems } = useReportDataPipeline({
    reportType,
    scopeLineItems: reportData?.scopeLineItems,
    diffScopeLineItems: diffReportData?.scopeLineItems,
  });
  const navigation = useReportNavigation(scanId, reportType, scan);
  const scrollAndHighlight = useScrollAndHighlight(scopeLineItems ?? []);

  // Don't show the dropdown if the scan is outdated
  const setCurrentDropdown = (dropdown: { groupId: string; taskId?: string } | undefined) => {
    if (scanScopeState !== 'outdated') _setCurrentDropdown(dropdown);
  };

  const changeOrderLookup = useMemo(
    () => buildChangeOrderLookupFromLineItems(diffScopeLineItems),
    [diffScopeLineItems],
  );

  const renderReportSection = () => {
    if (!scan || !scanRoot) {
      return <div css={LOADING_STYLE}>Loading scan data...</div>;
    }

    if (!reportData) {
      if (isReportDataLoading) {
        return <div css={LOADING_STYLE}>Loading print preview...</div>;
      }

      if (isReportDataError && reportDataError) {
        logError('Failed to load print preview', reportDataError, {
          scanId,
          reportType,
        });
        return <div css={LOADING_STYLE}>Failed to load print preview</div>;
      }

      return <div css={LOADING_STYLE}>No data available</div>;
    }

    return (
      <div ref={scrollAndHighlight.scrollContainerRef} css={CONTENT_SCROLL_STYLE}>
        <div css={USER_FEEDBACK_BANNER_STYLE(scan?.scanLifecycleStatus, reportType, scanScopeState)}>
          <div css={BANNER_TEXT_STYLE}>
            <strong>{getUserFeedbackDescriptionScopeState(scan?.scanLifecycleStatus, reportType, scanScopeState, reportData.overviewData.approvedDate)}</strong>
          </div>
        </div>
        <div css={CONTENT_WRAPPER_STYLE}>
          <div css={PAGE_CONTAINER_STYLE}>
            <div css={REACT_PRINT_STYLES.body}>
              {/* Change Order Report Section */}
              {reportType === 'changeOrder' && diffScopeLineItems && (
                <ReportSection
                  overviewData={reportData.overviewData}
                  sowData={scopeLineItems}
                  assignedContractorsWithTrades={reportData.assignedContractorsWithTrades}
                  logoSvg={reportData.orgSettings?.logoSvg}
                  includePhotos={reportData.orgSettings?.includeTaskPhotos}
                  isChangeOrderReport={true}
                  diffReportOverviewData={diffReportData?.overviewData}
                  diffScopeLineItems={diffScopeLineItems}
                  propertyProjectId={project?.id}
                />
              )}

              {/* Punchlist Report Section */}
              {reportType === 'punchlist' && scopeLineItems && (
                <ReportSection
                  overviewData={reportData.overviewData}
                  sowData={scopeLineItems}
                  assignedContractorsWithTrades={reportData.assignedContractorsWithTrades}
                  logoSvg={reportData.orgSettings?.logoSvg}
                  includePhotos={reportData.orgSettings?.includeTaskPhotos}
                  propertyProjectId={project?.id}
                />
              )}

              {/* Full Scope Report Section */}
              {reportType === 'fullScope' && scopeLineItems && (
                <ReportSection
                  overviewData={reportData.overviewData}
                  sowData={scopeLineItems}
                  assignedContractorsWithTrades={reportData.assignedContractorsWithTrades}
                  logoSvg={reportData.orgSettings?.logoSvg}
                  includePhotos={reportData.orgSettings?.includeTaskPhotos}
                  diffScopeLineItems={diffScopeLineItems}
                  propertyProjectId={project?.id}
                />
              )}

              {/* Closeout Report Section */}
              {reportType === 'closeout' && scopeLineItems && (
                <ReportSection
                  overviewData={reportData.overviewData}
                  sowData={scopeLineItems}
                  assignedContractorsWithTrades={reportData.assignedContractorsWithTrades}
                  logoSvg={reportData.orgSettings?.logoSvg}
                  includePhotos={reportData.orgSettings?.includeTaskPhotos}
                  diffScopeLineItems={diffScopeLineItems}
                  propertyProjectId={project?.id}
                />
              )}
            </div>
          </div>
          <div css={FOOTER_STYLE}>
            <div css={REACT_PRINT_STYLES.body}>
              <div
                css={[
                  REACT_PRINT_STYLES.borderBox,
                  reportData.orgSettings?.logoSvg && REACT_PRINT_STYLES.termsFooter,
                  css`width: 100%;`,
                ]}
              >
                <div css={REACT_PRINT_STYLES.rowBetween}>
                  <div css={REACT_PRINT_STYLES.borderBoxLabel}>
                    {reportData.overviewData?.responsibleOrg || ''} Terms and Conditions
                  </div>
                  {reportData.orgSettings?.logoSvg && (
                    <div css={REACT_PRINT_STYLES.orgLogo} dangerouslySetInnerHTML={{ __html: reportData.orgSettings.logoSvg }} />
                  )}
                </div>
                <div css={[REACT_PRINT_STYLES.borderBoxValue, REACT_PRINT_STYLES.tiny]}>
                  {CONTRACTOR_LEGAL_DISCLAIMER}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const contextValue = {
    scan,
    scanLifecycleStatus: scan?.scanLifecycleStatus,
    groupingMode,
    setGroupingMode,
    searchHighlightedTaskId: scrollAndHighlight.searchHighlightedTaskId,
    onLineItemClick: navigation.handleLineItemClick,
    reportType,
    searchableSowData: scopeLineItems,
    currentDropdown,
    setCurrentDropdown,
    changeOrderLookup,
  };

  return (
    <ScopePrintPreviewProvider value={contextValue}>
      <div css={PRINT_PREVIEW_CONTAINER_STYLE}>
        <ScopeToolbar />
        <div css={SCROLL_CONTAINER_WRAPPER_STYLE}>
          {renderReportSection()}
          <div
            css={SCROLL_TO_TOP_BUTTON_STYLE}
            onClick={scrollAndHighlight.handleScrollToTop}
            title='Scroll to top'
            style={{ opacity: scrollAndHighlight.showScrollToTop ? 1 : 0, pointerEvents: scrollAndHighlight.showScrollToTop ? 'auto' : 'none' }}
          >
            <ArrowUpIcon />
          </div>
        </div>
      </div>
    </ScopePrintPreviewProvider>
  );
};
